import {
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Package,
  Trash2,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Id } from "~/convex/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { api } from "~/convex/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useAccount } from "@/state/useAccount";
import { useAction } from "convex/react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";

interface StripeProduct {
  id: string;
  object: string;
  active: boolean;
  created: number;
  default_price?: string;
  description?: string;
  images: string[];
  livemode: boolean;
  metadata: Record<string, string>;
  name: string;
  package_dimensions?: {
    height: number;
    length: number;
    weight: number;
    width: number;
  } | null;
  shippable?: boolean;
  statement_descriptor?: string;
  tax_code?: string;
  type: string;
  unit_label?: string;
  updated: number;
  url?: string;
}

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/settings/affiliates",
)({
  component: AffiliatesSettings,
  beforeLoad: () => ({
    title: "Affiliates",
    headerTitle: "Affiliates",
    headerDescription: "Manage your affiliate system.",
  }),
});

export default function AffiliatesSettings() {
  const { toast } = useToast();
  const { appId } = useAccount();
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  // Campaign coupon configuration state
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>("");
  const [configForm, setConfigForm] = useState({
    isEnabled: false,
    discountType: "percentage" as "percentage" | "fixed",
    discountValue: 10,
    currency: "usd",
    selectedProducts: [] as string[],
    maxRedemptions: undefined as number | undefined,
    affiliateCommissionPercentage: 20,
    couponSuffix: "SAVE",
  });
  // Sales tax slider state (frontend only)
  const [salesTaxRate, setSalesTaxRate] = useState(10);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [isDeletingConfig, setIsDeletingConfig] = useState(false);
  const [editability, setEditability] = useState<{
    canEdit: boolean;
    hasActiveContracts: boolean;
  } | null>(null);

  // Convex actions
  const listProducts = useAction(api.stripeApp.listProducts);
  const saveCampaignCouponConfig = useAction(
    api.stripeApp.saveCampaignCouponConfig,
  );
  const deleteCampaignCouponConfig = useAction(
    api.stripeApp.deleteCampaignCouponConfig,
  );
  const checkCampaignEditability = useAction(
    api.stripeApp.checkCampaignEditability,
  );

  // Get current user data to access app information
  const { data: user } = useQuery({
    ...convexQuery(api.app.getCurrentUser, {}),
  });

  // Get current app details
  const currentApp = user?.appsList.find((app) => app._id === appId);

  // Get campaigns for the current app
  const { data: campaigns } = useQuery({
    ...convexQuery(api.campaigns.getAllCampaigns, {
      appId: appId as Id<"apps">,
    }),
    enabled: !!appId,
  });

  // Get coupon configuration for selected campaign
  const { data: campaignConfig } = useQuery({
    ...convexQuery(api.stripeApp.getCampaignCouponConfig, {
      campaignId: selectedCampaignId as Id<"campaigns">,
    }),
    enabled: !!selectedCampaignId,
  });

  // Check if user already has a connected Stripe account for this app
  const { data: affiliateAccount, isLoading } = useQuery({
    ...convexQuery(api.stripeApp.getUserAffiliateAccount, {
      appId: appId as Id<"apps">,
    }),
    enabled: !!appId,
  });

  // Check for success/error messages from OAuth redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get("success");
    const error = urlParams.get("error");

    if (success === "stripe_connected") {
      toast({
        title: "Success!",
        description: "Stripe account connected successfully",
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Load products after successful connection
      loadProducts();
    } else if (error) {
      toast({
        title: "Connection Failed",
        description: decodeURIComponent(error),
        variant: "destructive",
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [toast]);

  // Load products when affiliate account is available
  useEffect(() => {
    if (affiliateAccount && appId) {
      loadProducts();
    }
  }, [affiliateAccount, appId]);

  // Load campaign configuration when campaign is selected
  useEffect(() => {
    if (campaignConfig) {
      setConfigForm({
        isEnabled: campaignConfig.isEnabled,
        discountType: campaignConfig.discountType,
        discountValue:
          campaignConfig.discountType === "fixed"
            ? campaignConfig.discountValue / 100 // Convert from cents
            : campaignConfig.discountValue,
        currency: campaignConfig.currency || "usd",
        selectedProducts: campaignConfig.productIds || [],
        maxRedemptions: campaignConfig.maxRedemptions,
        affiliateCommissionPercentage:
          campaignConfig.affiliateCommissionPercentage || 20,
        couponSuffix: campaignConfig.couponSuffix || "SAVE",
      });
    } else if (selectedCampaignId) {
      // Reset to defaults if no config exists for selected campaign
      setConfigForm({
        isEnabled: false,
        discountType: "percentage",
        discountValue: 10,
        currency: "usd",
        selectedProducts: [],
        maxRedemptions: undefined,
        affiliateCommissionPercentage: 20,
        couponSuffix: "SAVE",
      });
    }
  }, [campaignConfig, selectedCampaignId]);

  // Check editability when campaign is selected
  useEffect(() => {
    if (selectedCampaignId) {
      const checkEditability = async () => {
        try {
          const result = await checkCampaignEditability({
            campaignId: selectedCampaignId as Id<"campaigns">,
          });
          setEditability(result);
        } catch (error) {
          console.error("Failed to check editability:", error);
          setEditability(null);
        }
      };
      checkEditability();
    } else {
      setEditability(null);
    }
  }, [selectedCampaignId, checkCampaignEditability]);

  // Function to save campaign coupon configuration
  const handleSaveConfig = async () => {
    if (!appId || !selectedCampaignId) return;

    setIsSavingConfig(true);
    try {
      const config = {
        isEnabled: configForm.isEnabled,
        discountType: configForm.discountType,
        discountValue:
          configForm.discountType === "fixed"
            ? Math.round(configForm.discountValue * 100) // Convert to cents for fixed amount
            : configForm.discountValue,
        currency:
          configForm.discountType === "fixed" ? configForm.currency : undefined,
        maxRedemptions: configForm.maxRedemptions,
        productIds:
          configForm.selectedProducts.length > 0
            ? configForm.selectedProducts
            : undefined,
        affiliateCommissionPercentage: configForm.affiliateCommissionPercentage,
        couponSuffix: configForm.couponSuffix,
      };

      await saveCampaignCouponConfig({
        campaignId: selectedCampaignId as Id<"campaigns">,
        appId: appId as Id<"apps">,
        config,
      });

      toast({
        title: "Success!",
        description: "Campaign coupon configuration saved successfully",
      });

      // Configuration saved successfully
    } catch (error) {
      console.error("Failed to save configuration:", error);
      toast({
        title: "Error",
        description: "Failed to save configuration",
        variant: "destructive",
      });
    } finally {
      setIsSavingConfig(false);
    }
  };

  // Function to update config form
  const updateConfigForm = (updates: Partial<typeof configForm>) => {
    setConfigForm((prev) => ({ ...prev, ...updates }));
  };

  // Function to delete campaign coupon configuration
  const handleDeleteConfig = async () => {
    if (!selectedCampaignId) return;

    if (
      !confirm(
        "Are you sure you want to delete this affiliate configuration? This action cannot be undone.",
      )
    ) {
      return;
    }

    setIsDeletingConfig(true);
    try {
      await deleteCampaignCouponConfig({
        campaignId: selectedCampaignId as Id<"campaigns">,
      });

      toast({
        title: "Success!",
        description: "Affiliate configuration deleted successfully",
      });

      // Reset form and refresh editability
      setConfigForm({
        isEnabled: false,
        discountType: "percentage",
        discountValue: 10,
        currency: "usd",
        selectedProducts: [],
        maxRedemptions: undefined,
        affiliateCommissionPercentage: 20,
        couponSuffix: "SAVE",
      });

      // Refresh editability check
      if (selectedCampaignId) {
        const result = await checkCampaignEditability({
          campaignId: selectedCampaignId as Id<"campaigns">,
        });
        setEditability(result);
      }
    } catch (error: unknown) {
      console.error("Failed to delete configuration:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to delete configuration",
        variant: "destructive",
      });
    } finally {
      setIsDeletingConfig(false);
    }
  };

  // Function to load products
  const loadProducts = async () => {
    if (!appId) return;

    setIsLoadingProducts(true);
    try {
      const result = await listProducts({ appId: appId as Id<"apps"> });
      if (result.success) {
        setProducts(result.products);
      }
    } catch (error) {
      console.error("Failed to load products:", error);
      toast({
        title: "Error",
        description: "Failed to load products",
        variant: "destructive",
      });
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Function to handle product selection
  const handleProductSelect = (productId: string, checked: boolean) => {
    updateConfigForm({
      selectedProducts: checked
        ? [...configForm.selectedProducts, productId]
        : configForm.selectedProducts.filter((id) => id !== productId),
    });
  };

  // Show loading state if no app selected
  if (!appId || isLoading) {
    return (
      <div className="flex h-full w-full flex-col gap-6">
        <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card p-6">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="text-sm text-primary/60">
              {!appId
                ? "Select an app to manage affiliate settings..."
                : "Loading..."}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-6">
      <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-lg font-medium">Stripe Account Connection</h3>
          {affiliateAccount && (
            <Badge
              variant={affiliateAccount.livemode ? "default" : "secondary"}
              className="flex items-center gap-1"
            >
              <CheckCircle className="h-3 w-3" />
              Connected {affiliateAccount.livemode ? "(Live)" : "(Test)"}
            </Badge>
          )}
        </div>

        {affiliateAccount ? (
          <div className="space-y-4 w-full">
            <p className="text-sm text-primary/60">
              Your Stripe account is connected to{" "}
              <strong>{currentApp?.name}</strong> and ready for affiliate
              features.
            </p>

            <div className="rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-green-900 dark:text-green-100">
                    Account Connected
                  </h4>
                  <div className="text-sm text-green-700 dark:text-green-300 mt-1 space-y-1">
                    <p>
                      <strong>Account ID:</strong> {affiliateAccount.accountId}
                    </p>
                    <p>
                      <strong>Mode:</strong>{" "}
                      {affiliateAccount.livemode ? "Live" : "Test"}
                    </p>
                    <p>
                      <strong>Scope:</strong> {affiliateAccount.scope}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <a
                href={`https://dashboard.stripe.com/${affiliateAccount.livemode ? "" : "test/"}dashboard`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Stripe Dashboard
              </a>
            </div>
          </div>
        ) : (
          <div className="space-y-4 w-full">
            <p className="text-sm text-primary/60">
              Connect your Stripe account to <strong>{currentApp?.name}</strong>{" "}
              to enable affiliate features like coupon codes and referral
              tracking for this app.
            </p>

            <div className="rounded-lg bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-900 dark:text-yellow-100">
                    Account Not Connected
                  </h4>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    You need to connect your Stripe account to{" "}
                    <strong>{currentApp?.name}</strong> to access affiliate
                    features for this app.
                  </p>
                </div>
              </div>
            </div>

            <a
              href={`https://marketplace.stripe.com/oauth/v2/chnlink_61T53D5jRoDKTXjZg41BojZJZlfN7HHs/authorize?client_id=${window.location.hostname === "creatorgrid.io" ? "ca_SrO13lrWV9J3BQMbUYKZo3qpa16t6KDT" : "ca_SrO1kH6LsaiIrDmDuhNkWiO7EQwDsRnM"}&redirect_uri=${encodeURIComponent(window.location.origin + "/affiliates")}&state=${appId}`}
              className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Connect Stripe Account for {currentApp?.name || "this app"}
            </a>
          </div>
        )}

        {/* Campaign Coupon Configuration Section */}
        {affiliateAccount && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium">
                  Affiliate Coupon Configuration
                </h3>
                <p className="text-sm text-gray-600">
                  Configure automatic coupon generation for creators who join
                  your campaigns. Each creator will get their own unique coupon
                  code based on these settings.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={loadProducts}
                  disabled={isLoadingProducts}
                  variant="outline"
                  size="sm"
                >
                  {isLoadingProducts
                    ? "Loading Products..."
                    : "Refresh Products"}
                </Button>
              </div>
            </div>

            {/* Campaign Selection and Configuration */}
            <div className="space-y-6">
              {/* Campaign Selector */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Select Campaign</CardTitle>
                  <CardDescription>
                    Choose a campaign to configure affiliate coupon settings
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {campaigns && campaigns.length > 0 ? (
                    <Select
                      value={selectedCampaignId}
                      onValueChange={setSelectedCampaignId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a campaign..." />
                      </SelectTrigger>
                      <SelectContent>
                        {campaigns.map((campaign) => (
                          <SelectItem key={campaign._id} value={campaign._id}>
                            <div className="flex items-center gap-2">
                              <span>{campaign.name}</span>
                              <Badge
                                variant={
                                  campaign.status === "active"
                                    ? "default"
                                    : "secondary"
                                }
                                className="text-xs"
                              >
                                {campaign.status}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="text-sm text-gray-600">
                      No campaigns found. Create a campaign first to configure
                      affiliate coupons.
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Configuration Form */}
              {selectedCampaignId && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">
                          Coupon Configuration
                        </CardTitle>
                        <CardDescription>
                          Configure how affiliate coupons will be generated for
                          this campaign
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        {campaignConfig && (
                          <Badge
                            variant={
                              campaignConfig.isEnabled ? "default" : "secondary"
                            }
                          >
                            {campaignConfig.isEnabled ? "Enabled" : "Disabled"}
                          </Badge>
                        )}
                        {editability?.hasActiveContracts && (
                          <Badge variant="destructive" className="text-xs">
                            Locked - Active Contracts
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Active Contracts Warning - only show for existing configs */}
                    {campaignConfig && editability?.hasActiveContracts && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="text-sm font-medium text-amber-800">
                              Configuration Locked
                            </h4>
                            <p className="text-sm text-amber-700 mt-1">
                              This existing configuration cannot be edited or
                              deleted because the campaign has active creator
                              contracts. Please wait until the campaign ends or
                              contact support if you need to make changes.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm font-medium">
                          Enable Affiliate Coupons
                        </Label>
                        <p className="text-sm text-gray-600">
                          Automatically generate discount coupons for creators
                          who join this campaign
                        </p>
                      </div>
                      <Switch
                        checked={configForm.isEnabled}
                        onCheckedChange={(checked) =>
                          updateConfigForm({ isEnabled: checked })
                        }
                        disabled={
                          !!(campaignConfig && editability?.hasActiveContracts)
                        }
                      />
                    </div>

                    {configForm.isEnabled && (
                      <>
                        {/* Coupon Suffix */}
                        <div className="grid gap-2">
                          <Label>Coupon Code Suffix</Label>
                          <Input
                            placeholder="e.g., SAVE, DISCOUNT, OFF"
                            value={configForm.couponSuffix}
                            onChange={(e) =>
                              updateConfigForm({ couponSuffix: e.target.value })
                            }
                            disabled={
                              !!(
                                campaignConfig &&
                                editability?.hasActiveContracts
                              )
                            }
                          />
                          <p className="text-xs text-gray-600">
                            Generated codes will look like: CREATORNAME-
                            {configForm.couponSuffix}
                          </p>
                        </div>

                        {/* Discount Type and Value */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="grid gap-2">
                            <Label>Discount Type</Label>
                            <Select
                              value={configForm.discountType}
                              onValueChange={(value: "percentage" | "fixed") =>
                                updateConfigForm({ discountType: value })
                              }
                              disabled={
                                !!(
                                  campaignConfig &&
                                  editability?.hasActiveContracts
                                )
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percentage">
                                  Percentage
                                </SelectItem>
                                <SelectItem value="fixed">
                                  Fixed Amount
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid gap-2">
                            <Label>Discount Value</Label>
                            <Input
                              type="number"
                              min="1"
                              max={
                                configForm.discountType === "percentage"
                                  ? 100
                                  : undefined
                              }
                              value={configForm.discountValue}
                              onChange={(e) =>
                                updateConfigForm({
                                  discountValue: parseInt(e.target.value) || 0,
                                })
                              }
                              disabled={
                                !!(
                                  campaignConfig &&
                                  editability?.hasActiveContracts
                                )
                              }
                            />
                          </div>
                          {configForm.discountType === "fixed" && (
                            <div className="grid gap-2">
                              <Label>Currency</Label>
                              <Select
                                value={configForm.currency}
                                onValueChange={(value) =>
                                  updateConfigForm({ currency: value })
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="usd">USD</SelectItem>
                                  <SelectItem value="eur">EUR</SelectItem>
                                  <SelectItem value="gbp">GBP</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>

                        {/* Affiliate Commission */}
                        <div className="grid gap-2">
                          <Label>Affiliate Commission (%)</Label>
                          <Input
                            type="number"
                            min="1"
                            max="100"
                            value={configForm.affiliateCommissionPercentage}
                            onChange={(e) =>
                              updateConfigForm({
                                affiliateCommissionPercentage:
                                  parseInt(e.target.value) || 20,
                              })
                            }
                            disabled={
                              !!(
                                campaignConfig &&
                                editability?.hasActiveContracts
                              )
                            }
                          />
                          <p className="text-xs text-gray-600">
                            Percentage of the discounted price (including tax)
                            that the affiliate receives as commission
                          </p>
                        </div>

                        {/* Sales Tax Rate Slider */}
                        <div className="grid gap-2">
                          <Label>Sales Tax Rate (%)</Label>
                          <div className="px-2">
                            <Slider
                              value={[salesTaxRate]}
                              onValueChange={(value) =>
                                setSalesTaxRate(value[0])
                              }
                              max={30}
                              min={0}
                              step={1}
                              className="w-full"
                            />
                          </div>
                          <div className="flex justify-between text-xs text-gray-600">
                            <span>0%</span>
                            <span>30%</span>
                          </div>
                          <p className="text-xs text-gray-600">
                            Adjust this to match your local tax rate for
                            accurate commission previews
                          </p>
                        </div>

                        {/* Commission Preview */}
                        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                          <h4 className="text-sm font-medium mb-2">
                            Commission Preview
                          </h4>
                          <div className="space-y-3 text-sm">
                            {products.length > 0 ? (
                              <>
                                <div className="text-gray-600 dark:text-gray-300 font-medium">
                                  Based on your products (estimated pricing):
                                </div>
                                <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded p-2">
                                  💡 Prices shown are estimates for preview.
                                  Actual commission will be calculated using
                                  real Stripe pricing.
                                </div>
                                {(configForm.selectedProducts.length > 0
                                  ? products.filter((p) =>
                                      configForm.selectedProducts.includes(
                                        p.id,
                                      ),
                                    )
                                  : products.slice(0, 3)
                                ).map((product, index) => {
                                  // Note: Using estimated prices for preview. In production, fetch actual prices from Stripe
                                  const estimatedPrices = [
                                    9.99, 19.99, 29.99, 49.99, 99.99,
                                  ];
                                  const basePrice =
                                    estimatedPrices[
                                      index % estimatedPrices.length
                                    ];

                                  // Calculate discounted price
                                  const discountedPrice =
                                    configForm.discountType === "percentage"
                                      ? basePrice *
                                        (1 - configForm.discountValue / 100)
                                      : Math.max(
                                          0,
                                          basePrice -
                                            configForm.discountValue / 100,
                                        );

                                  // Calculate tax on DISCOUNTED price (correct calculation)
                                  const priceWithTax =
                                    discountedPrice * (1 - salesTaxRate / 100); // Dynamic tax reduction on discounted amount

                                  // Calculate affiliate commission
                                  const affiliateCommission =
                                    priceWithTax *
                                    (configForm.affiliateCommissionPercentage /
                                      100);

                                  return (
                                    <div
                                      key={product.id}
                                      className="border-l-2 border-blue-200 dark:border-blue-600 pl-3 space-y-1"
                                    >
                                      <div className="font-medium text-gray-700 dark:text-gray-200">
                                        {product.name}
                                      </div>
                                      <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                                        <div>
                                          • Original price: $
                                          {basePrice.toFixed(2)}
                                        </div>
                                        <div>
                                          • After{" "}
                                          {configForm.discountType ===
                                          "percentage"
                                            ? `${configForm.discountValue}%`
                                            : `$${(configForm.discountValue / 100).toFixed(2)}`}{" "}
                                          discount: $
                                          {discountedPrice.toFixed(2)}
                                        </div>
                                        <div>
                                          • With {salesTaxRate}% sales tax on
                                          discounted price: $
                                          {priceWithTax.toFixed(2)}
                                        </div>
                                        <div className="font-medium text-green-600">
                                          • Affiliate earns: $
                                          {affiliateCommission.toFixed(2)} (
                                          {
                                            configForm.affiliateCommissionPercentage
                                          }
                                          % of final price)
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                                {configForm.selectedProducts.length === 0 &&
                                  products.length > 3 && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                                      Showing first 3 products. Select specific
                                      products above to see targeted
                                      calculations.
                                    </div>
                                  )}
                              </>
                            ) : (
                              <div className="text-gray-500 dark:text-gray-400">
                                Load products to see commission calculations
                                based on actual pricing
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Product Selection */}
                        <div className="grid gap-2">
                          <Label>Apply to Products (optional)</Label>
                          <p className="text-sm text-gray-600 mb-2">
                            Leave empty to apply to all products, or select
                            specific products below
                          </p>
                          {isLoadingProducts ? (
                            <div className="text-sm text-gray-600">
                              Loading products...
                            </div>
                          ) : products.length > 0 ? (
                            <div className="max-h-48 overflow-y-auto border rounded-md p-3 space-y-2">
                              {products.map((product) => (
                                <div
                                  key={product.id}
                                  className="flex items-center space-x-2"
                                >
                                  <Checkbox
                                    id={`config-product-${product.id}`}
                                    checked={configForm.selectedProducts.includes(
                                      product.id,
                                    )}
                                    onCheckedChange={(checked) =>
                                      handleProductSelect(product.id, !!checked)
                                    }
                                  />
                                  <Label
                                    htmlFor={`config-product-${product.id}`}
                                    className="text-sm font-normal flex-1 cursor-pointer"
                                  >
                                    <div className="flex items-center gap-2">
                                      {product.images.length > 0 ? (
                                        <img
                                          src={product.images[0]}
                                          alt={product.name}
                                          className="w-6 h-6 rounded object-cover"
                                        />
                                      ) : (
                                        <Package className="h-4 w-4 text-gray-400" />
                                      )}
                                      <span>{product.name}</span>
                                      <Badge
                                        variant="secondary"
                                        className="text-xs"
                                      >
                                        {product.id}
                                      </Badge>
                                    </div>
                                  </Label>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-600">
                              No products found
                            </div>
                          )}
                        </div>

                        {/* Max Redemptions */}
                        <div className="grid gap-2">
                          <Label>Max Redemptions per Coupon (optional)</Label>
                          <Input
                            type="number"
                            min="1"
                            placeholder="Leave empty for unlimited"
                            value={configForm.maxRedemptions || ""}
                            onChange={(e) =>
                              updateConfigForm({
                                maxRedemptions: e.target.value
                                  ? parseInt(e.target.value)
                                  : undefined,
                              })
                            }
                          />
                          <p className="text-xs text-gray-600">
                            How many times each creator's coupon can be used
                          </p>
                        </div>
                      </>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-between items-center pt-4 border-t">
                      <div>
                        {campaignConfig && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDeleteConfig}
                            disabled={
                              isDeletingConfig ||
                              (campaignConfig &&
                                editability?.hasActiveContracts) ||
                              !selectedCampaignId
                            }
                            className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                          >
                            {isDeletingConfig ? (
                              "Deleting..."
                            ) : (
                              <>
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Configuration
                              </>
                            )}
                          </Button>
                        )}
                      </div>

                      <Button
                        onClick={handleSaveConfig}
                        disabled={
                          isSavingConfig ||
                          (campaignConfig && editability?.hasActiveContracts) ||
                          !selectedCampaignId
                        }
                      >
                        {isSavingConfig
                          ? "Saving..."
                          : campaignConfig
                            ? "Update Configuration"
                            : "Create Configuration"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
